import React, { useState } from "react";
import { motion } from "framer-motion";
import { openExternalLink } from "../../../services/site/linkService";
import { InfoService } from "../../../services/site/InfoService";

interface BookingPlan {
  name: string;
  price: number | "custom";
  tag: string;
  features: string[];
  recommended?: boolean;
  enterprise?: boolean;
}

const PLANS: BookingPlan[] = [
  {
    name: "Starter",
    price: 999,
    tag: "Up to 200 orders / mo",
    features: [
      "WhatsApp Ordering",
      "Kitchen Display (KDS)",
      "Basic reports",
      "Single outlet",
      "Email support",
    ],
  },
  {
    name: "Growth",
    price: 2499,
    tag: "Up to 1,000 orders / mo",
    recommended: true,
    features: [
      "Everything in Starter",
      "Raw materials + recipes",
      "Vendor module + POs",
      "Gmail invoice auto-parse",
      "Coupons & campaigns",
    ],
  },
  {
    name: "Pro",
    price: 4999,
    tag: "Unlimited orders",
    features: [
      "Everything in Growth",
      "AI Report Chat",
      "AI helpers (HSN, recipes, expenses)",
      "Full analytics suite",
      "Priority support",
    ],
  },
  {
    name: "Enterprise",
    price: "custom",
    tag: "Multi-outlet & franchise",
    enterprise: true,
    features: [
      "White-label theme & domain",
      "Multi-outlet franchise dashboard",
      "Custom integrations",
      "Dedicated success manager",
      "SLA & on-prem options",
    ],
  },
];

const BookingPricing: React.FC = () => {
  const [yearly, setYearly] = useState(false);
  const yearlyDiscount = 0.15;

  const formatPrice = (p: BookingPlan) => {
    if (p.price === "custom") return null;
    const monthly = p.price as number;
    const display = yearly ? Math.round(monthly * 12 * (1 - yearlyDiscount)) : monthly;
    return display;
  };

  return (
    <div>
      <div className="plan-upgrade-billing-toggle has-text-centered mb-5">
        <span className={!yearly ? "is-active" : ""} style={{ marginRight: 12, fontWeight: 600 }}>
          Monthly
        </span>
        <label className="plan-upgrade-switch" style={{ verticalAlign: "middle" }}>
          <input
            type="checkbox"
            checked={yearly}
            onChange={() => setYearly(!yearly)}
          />
          <span className="plan-upgrade-slider"></span>
        </label>
        <span className={yearly ? "is-active" : ""} style={{ marginLeft: 12, fontWeight: 600 }}>
          Yearly <span className="tag is-success is-light ml-2">Save 15%</span>
        </span>
      </div>

      <div className="sds_bk-pricing-grid">
        {PLANS.map((plan, idx) => {
          const display = formatPrice(plan);
          return (
            <motion.div
              key={plan.name}
              className={`sds_bk-plan-card ${plan.recommended ? "recommended" : ""} ${
                plan.enterprise ? "enterprise" : ""
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
            >
              {plan.recommended && (
                <div className="sds_bk-plan-ribbon">★ Most Popular</div>
              )}
              <div className="sds_bk-plan-name">{plan.name}</div>
              <div className="sds_bk-plan-tag">{plan.tag}</div>

              <div className="sds_bk-plan-price">
                {display != null ? (
                  <>
                    <span className="currency">₹</span>
                    <span className="amount">{display.toLocaleString("en-IN")}</span>
                    <span className="interval">/{yearly ? "year" : "month"}</span>
                  </>
                ) : (
                  <span className="amount" style={{ fontSize: "1.5rem" }}>
                    Custom
                  </span>
                )}
              </div>

              <ul className="sds_bk-plan-features">
                {plan.features.map((f, i) => (
                  <li key={i}>
                    <span className="feature-check">✓</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                className="sds_bk-plan-btn"
                onClick={() => openExternalLink(InfoService?.bookingLink)}
              >
                {plan.enterprise ? "Talk to Sales" : "Start Free Trial"}
              </button>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default BookingPricing;
